/**
 * EmployeesSettingsHub — iter-218 (Feb 2026)
 *
 * New intermediate hub between "التحكم بإدارة الشركة" and per-employee
 * configuration screens. Currently exposes:
 *   • إعدادات التوقيع والختم (moved out of Employee Dashboard as of iter-218
 *     — the user asked that only the primary admin controls this).
 *
 * Route: /control-panel-settings/company-mgmt/employees
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PenLine, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function EmployeesSettingsHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  // iter-218c (Feb 2026) — Signature & Stamp were originally two SEPARATE cards.
  // iter-218r (Feb 2026) — The Stamp card moved to the new "ختم ومسمى الشركة"
  // (Company Branding) hub per user request. Only the per-admin Signature
  // management remains here.
  const CARDS = [
    {
      id: 'signature',
      testid: 'employees-signature-card',
      titleAr: 'التوقيع',
      titleEn: 'Signatures',
      descAr:  'إدارة توقيع كل موظف مع اسمه ومسماه الوظيفي بالعربي والإنجليزي',
      descEn:  'Manage per-employee signatures with Arabic + English name & job title',
      icon: PenLine,
      color: '#8B5CF6',
      route: '/control-panel-settings/company-mgmt/employees/signature',
    },
  ];

  return (
    <DashboardLayout
      title="إعدادات الموظفين"
      titleEn="Employees Settings"
      subtitle="إعدادات خاصة بالمشرفين والموظفين — التوقيع والختم"
      subtitleEn="Supervisor & staff settings — signature and stamp"
      screenCode="EMP-SETTINGS"
      backPath="/control-panel-settings/company-mgmt"
      onBack={() => navigate('/control-panel-settings/company-mgmt')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control', path: '/control-panel-settings/company-mgmt' },
        { labelAr: 'إعدادات الموظفين', labelEn: 'Employees Settings' },
      ]}
    >
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="employees-settings-hub">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate(c.route)}
              className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all text-start"
              style={{ borderColor: `${c.color}30` }}
              data-testid={c.testid}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                     style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}40` }}>
                  <c.icon className="h-7 w-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                    {isAr ? c.titleAr : c.titleEn}
                    <Arrow className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.color }} />
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {isAr ? c.descAr : c.descEn}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
